import { Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
	useAssignCommitmentMatrixToPerson,
	useCommitmentMatrixAssignmentPeople,
	useSetCommitmentMatrixExemptForPerson,
} from '@/api/queries/commitment-matrix-queries'
import { useJobGroups } from '@/api/queries/job-group-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import {
	AssignmentPeopleTable,
	type AssignmentSortState,
} from '@/components/administration/commitment-matrix/assignment/assignment-people-table'
import { SearchInput } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import type {
	CommitmentMatrix,
	CommitmentMatrixAssignmentPerson,
	CommitmentMatrixAssignmentSortColumn,
} from '@/types'

interface CommitmentMatrixAssignmentTabProps {
	matrices: CommitmentMatrix[]
	branchId?: string
	crId?: string
}

const UNASSIGNED = '__unassigned__'
const PAGE_SIZE = 100

const DEFAULT_SORT: AssignmentSortState = { column: 'name', direction: 'asc' }

export function CommitmentMatrixAssignmentTab({
	matrices,
	branchId: scopeBranchId,
	crId: scopeCrId,
}: CommitmentMatrixAssignmentTabProps) {
	const [search, setSearch] = useState('')
	const [branchFilter, setBranchFilter] = useState<string>('')
	const [crFilter, setCrFilter] = useState<string>('')
	const [jobGroupFilter, setJobGroupFilter] = useState<string>('')
	const [exemptFilter, setExemptFilter] = useState<boolean | undefined>(
		undefined,
	)
	const [directAssignmentFilter, setDirectAssignmentFilter] = useState<
		boolean | undefined
	>(undefined)
	const [page, setPage] = useState(1)
	const [sort, setSort] = useState<AssignmentSortState>(DEFAULT_SORT)

	const hasScope = !!(scopeCrId || scopeBranchId)
	const effectiveCrId = scopeCrId ?? crFilter
	const effectiveBranchId = scopeCrId
		? undefined
		: (scopeBranchId ?? branchFilter)

	const { data: branches } = useOrganization()
	const { data: jobGroups } = useJobGroups()
	const { data, isLoading, isFetching, isPlaceholderData } =
		useCommitmentMatrixAssignmentPeople({
			q: search || undefined,
			branchId: effectiveCrId ? undefined : effectiveBranchId || undefined,
			crId: effectiveCrId || undefined,
			jobGroupId: jobGroupFilter || undefined,
			matrixExempt: exemptFilter,
			hasDirectAssignment: directAssignmentFilter,
			page,
			pageSize: PAGE_SIZE,
			sortBy: sort.column,
			sortDir: sort.direction,
		})
	const assignMutation = useAssignCommitmentMatrixToPerson()
	const exemptMutation = useSetCommitmentMatrixExemptForPerson()

	const people = data?.items ?? []
	const total = data?.total ?? 0
	const totalPages = data?.totalPages ?? 0
	const currentPage = data?.page ?? page

	const activeMatrices = matrices.filter((m) => m.active)

	const branchOptions = useMemo(
		() => (branches ?? []).map((b) => ({ value: b.id, label: b.name })),
		[branches],
	)
	const crOptions = useMemo(
		() =>
			(branches ?? []).flatMap((b) =>
				b.crs.map((c) => ({ value: c.id, label: c.name })),
			),
		[branches],
	)
	const jobGroupOptions = useMemo(
		() => (jobGroups ?? []).map((g) => ({ value: g.id, label: g.name })),
		[jobGroups],
	)
	const matrixOptions = useMemo(
		() => [
			{ value: UNASSIGNED, label: 'Sem atribuição direta' },
			...activeMatrices.map((matrix) => ({
				value: matrix.id,
				label: matrix.name,
			})),
		],
		[activeMatrices],
	)
	const matrixNameById = useMemo(
		() => new Map(matrices.map((m) => [m.id, m.name] as const)),
		[matrices],
	)

	const handleBranchChange = (value: string) => {
		setBranchFilter(value)
		setPage(1)
	}
	const handleCrChange = (value: string) => {
		setCrFilter(value)
		setPage(1)
	}
	const handleJobGroupChange = (value: string) => {
		setJobGroupFilter(value)
		setPage(1)
	}
	const handleExemptChange = (value: string) => {
		setExemptFilter(value === '' ? undefined : value === 'true')
		setPage(1)
	}
	const handleDirectAssignmentChange = (value: string) => {
		setDirectAssignmentFilter(value === '' ? undefined : value === 'true')
		setPage(1)
	}
	const handleSearchChange = (value: string) => {
		setSearch(value)
		setPage(1)
	}

	const handleSort = (column: CommitmentMatrixAssignmentSortColumn) => {
		setSort((current) => {
			if (current.column !== column) {
				return { column, direction: 'asc' }
			}
			return {
				column,
				direction: current.direction === 'asc' ? 'desc' : 'asc',
			}
		})
		setPage(1)
	}

	const handleAssign = (personId: string, value: string) => {
		const matrixId = value === UNASSIGNED ? null : value
		const matrixName = matrixNameById.get(matrixId ?? '')
		assignMutation.mutate(
			{ personId, body: { matrixId } },
			{
				onSuccess: () => {
					toast.success(
						matrixId
							? `Matriz "${matrixName}" atribuída.`
							: 'Atribuição removida.',
					)
				},
				onError: () => {
					toast.error('Não foi possível atualizar a atribuição.')
				},
			},
		)
	}

	const handleToggleExempt = (
		person: CommitmentMatrixAssignmentPerson,
		exempt: boolean,
	) => {
		exemptMutation.mutate(
			{ personId: person.id, body: { matrixExempt: exempt } },
			{
				onSuccess: () => {
					toast.success(
						exempt
							? `${person.name} isento(a) de matriz de comprometimento.`
							: `Isenção removida de ${person.name}.`,
					)
				},
				onError: () => {
					toast.error('Não foi possível atualizar a isenção.')
				},
			},
		)
	}

	return (
		<div className="flex flex-col gap-4">
			<p className="text-xs text-muted-foreground">
				Busque colaboradores por Filial, CR e/ou Grupo de Cargo e atribua
				diretamente uma matriz de comprometimento a cada um. A coluna "Matriz
				aplicada" mostra a matriz que efetivamente vale para o colaborador,
				seguindo a ordem de precedência{' '}
				<strong>Pessoal &gt; CR &gt; Grupo &gt; Filial</strong> — o nível mais
				específico com matriz ativa vence.
			</p>

			<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
				<SearchInput
					value={search}
					onChange={handleSearchChange}
					placeholder="Buscar colaborador..."
					className="sm:flex-1 sm:basis-full"
				/>
				{!hasScope && (
					<>
						<SearchableSelect
							options={branchOptions}
							value={crFilter ? '' : branchFilter}
							onValueChange={handleBranchChange}
							placeholder="Todas as filiais"
							searchPlaceholder="Buscar filial..."
							emptyMessage="Nenhuma filial encontrada"
							disabled={!!crFilter}
							className="sm:w-56"
						/>
						<SearchableSelect
							options={crOptions}
							value={crFilter}
							onValueChange={handleCrChange}
							placeholder="Todos os CRs"
							searchPlaceholder="Buscar CR..."
							emptyMessage="Nenhum CR encontrado"
							className="sm:w-56"
						/>
					</>
				)}
				<SearchableSelect
					options={jobGroupOptions}
					value={jobGroupFilter}
					onValueChange={handleJobGroupChange}
					placeholder="Todos os grupos"
					searchPlaceholder="Buscar grupo..."
					emptyMessage="Nenhum grupo encontrado"
					className="sm:w-56"
				/>

				<div className="flex items-center gap-1.5">
					<span className="text-xs text-muted-foreground">Isento:</span>
					<ToggleGroup
						type="single"
						variant="outline"
						size="sm"
						value={exemptFilter === undefined ? '' : String(exemptFilter)}
						onValueChange={handleExemptChange}
					>
						<ToggleGroupItem value="">Todos</ToggleGroupItem>
						<ToggleGroupItem value="true">Sim</ToggleGroupItem>
						<ToggleGroupItem value="false">Não</ToggleGroupItem>
					</ToggleGroup>
				</div>

				<div className="flex items-center gap-1.5">
					<span className="text-xs text-muted-foreground">
						Atribuição direta:
					</span>
					<ToggleGroup
						type="single"
						variant="outline"
						size="sm"
						value={
							directAssignmentFilter === undefined
								? ''
								: String(directAssignmentFilter)
						}
						onValueChange={handleDirectAssignmentChange}
					>
						<ToggleGroupItem value="">Todos</ToggleGroupItem>
						<ToggleGroupItem value="true">Sim</ToggleGroupItem>
						<ToggleGroupItem value="false">Não</ToggleGroupItem>
					</ToggleGroup>
				</div>
			</div>

			<AssignmentPeopleTable
				people={people}
				isLoading={isLoading}
				isFetching={isFetching}
				sort={sort}
				onSort={handleSort}
				matrixOptions={matrixOptions}
				onAssign={handleAssign}
				onToggleExempt={handleToggleExempt}
			/>

			{!isLoading && total > 0 && (
				<div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
					<span className="flex items-center gap-1.5">
						<Users className="size-3.5" />
						{total} colaborador{total === 1 ? '' : 'es'} encontrado
						{total === 1 ? '' : 's'} · página {currentPage} de {totalPages}
					</span>
					<div className="flex gap-2">
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={currentPage <= 1 || isPlaceholderData}
							onClick={() => setPage((p) => Math.max(1, p - 1))}
						>
							‹ Anterior
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={currentPage >= totalPages || isPlaceholderData}
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						>
							Próxima ›
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}
